import { createQueries } from '@sl/core';
import type { ApiClient } from '@sl/core';
import type { QueryClient } from '@tanstack/react-query';
import { routesTable } from '../routes.table';

// WARM START — the shell's two unconditional reads begin the moment the client exists, not when React
// reaches them (T-321 / BUG-155; REQ-280 AC1).
//
// **The chain this shortens.** On a framed route nothing can be drawn until the session has answered, and no
// number can be ASKED FOR until the saved preferences have (`PeriodProvider`'s `settling`: a range the user
// never chose must not be fetched and then changed under them — T-037's rule). Those two reads were issued by
// components: `AppShell` mounts, suspends on `useApiClient()` while the demo chunk is still arriving, renders
// its skeleton, and only then asks for the session; `PeriodProvider` mounts under the ANSWER to that session
// and only then asks for the preferences. So three things that do not depend on each other — React's mount
// and first paint, the session read, the preferences read — ran strictly one after another, and on a cold
// throttled load the gap between "the client is ready" and "the server has been asked anything" was most of
// a quarter of a second.
//
// **What this does, and what it deliberately does not.** It is a `prefetchQuery` of the app's own two reads,
// through the app's own factories (`@sl/core/queries`), under the same keys the components use — so the
// components find the read already in flight and await the same promise. No endpoint, no key, no option and
// no screen changes; nothing new is requested, and nothing is requested twice. It does not touch anything
// account-scoped (`queries.summary` and friends need the account id and the resolved range, which only exist
// after these two have answered), and it never asks on a route that has no shell: a reader who opens the
// welcome page and stays there is unchanged (ARCH §9.1 — Welcome is outside the shell and reads nothing).
//
// Failures are swallowed by `prefetchQuery` itself, exactly as they are for the idle period prefetch: the
// components' own `useQuery` is what reports an error to the reader (`ShellError`), and a prefetch that
// rejected simply leaves them to ask again.

/** Does the address this load started at land on a route the shell frames (ARCH §9.2 — hash routing)? */
export function startsInsideShell(location: { hash: string }): boolean {
  // `#/home?x=1` → `/home`; an empty hash is the welcome route, which `routesTable` carries as `shell: false`.
  const raw = location.hash.replace(/^#/, '').split('?')[0] ?? '';
  const path = raw === '' ? '/' : raw;
  const entry = routesTable.find((route) => matches(route.path, path));
  return entry?.shell ?? false;
}

/** `/entity/:id` vs `/entity/ad-1` — segment by segment, a `:param` matching any one segment. */
function matches(pattern: string, path: string): boolean {
  const a = pattern.split('/');
  const b = path.split('/');
  return a.length === b.length && a.every((part, i) => part.startsWith(':') || part === b[i]);
}

/**
 * Start the session and preferences reads as soon as `client` resolves, if this load started on a framed
 * route. Returns a promise for tests; nothing awaits it in the app.
 */
export function warmStart(
  queryClient: QueryClient,
  client: Promise<ApiClient>,
  location: { hash: string },
): Promise<void> {
  if (!startsInsideShell(location)) return Promise.resolve();
  return client
    .then((ready) => {
      // The same bootstrap factories `AppShell` and `PeriodProvider` use: `createQueries(client)` with no
      // options is `ShellBody`'s own `bootstrap`, and `preferences()` is account-independent by contract.
      const queries = createQueries(ready);
      return Promise.all([
        queryClient.prefetchQuery(queries.session()),
        queryClient.prefetchQuery(queries.preferences()),
      ]);
    })
    .then(() => undefined)
    .catch(() => undefined);
}
